import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  type AppliedReviewFilters,
  type ReviewFilterSnapshot,
  type SavedPreset,
  pushHistory,
} from "../utils/filter-presets";

export const INITIAL_APPLIED: AppliedReviewFilters = { limit: 100 };

interface ReviewFilterState {
  // ─── Live state (was useState in useReviewFilters) ───────────────
  selectedLocationId?: number;
  selectedMachineIds: number[];
  appliedFilters: AppliedReviewFilters;

  // ─── Persisted presets + history ─────────────────────────────────
  savedPresets: SavedPreset[];
  recentHistory: ReviewFilterSnapshot[];

  // ─── Live setters ────────────────────────────────────────────────
  setSelectedLocationId: (id: number | undefined) => void;
  setSelectedMachineIds: (ids: number[]) => void;
  setAppliedFilters: (next: AppliedReviewFilters) => void;

  // ─── Preset/history actions ──────────────────────────────────────
  savePreset: (name: string, snapshot: ReviewFilterSnapshot) => void;
  deletePreset: (id: string) => void;
  recordHistory: (snapshot: ReviewFilterSnapshot) => void;
}

/**
 * The live filter state (scope + appliedFilters) is intentionally persisted
 * alongside saved presets and recent history, so a returning user resumes
 * their last view instead of a blank slate.
 */
export const useReviewFilterStore = create<ReviewFilterState>()(
  persist(
    (set) => ({
      selectedLocationId: undefined,
      selectedMachineIds: [],
      appliedFilters: { ...INITIAL_APPLIED },
      savedPresets: [],
      recentHistory: [],

      setSelectedLocationId: (id) => set({ selectedLocationId: id }),
      setSelectedMachineIds: (ids) => set({ selectedMachineIds: ids }),
      setAppliedFilters: (next) => set({ appliedFilters: next }),

      savePreset: (name, snapshot) =>
        set((s) => ({
          savedPresets: [
            {
              id: crypto.randomUUID(),
              name,
              snapshot,
              createdAt: new Date().toISOString(),
            },
            ...s.savedPresets,
          ],
        })),
      deletePreset: (id) =>
        set((s) => ({
          savedPresets: s.savedPresets.filter((p) => p.id !== id),
        })),
      recordHistory: (snapshot) =>
        set((s) => ({ recentHistory: pushHistory(s.recentHistory, snapshot) })),
    }),
    { name: "ml-review-filters" },
  ),
);
